import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Settings, X, Truck, AlertTriangle } from "lucide-react";
import StickyHeader from "../../../components/ModuleCommon/StickyHeader/StickyHeader";
import notify from "../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import Button from "../../../components/ACS-Dashboard/ACS-Blocks/Button";
import { addTab as addShipmentTab } from "../../../redux/slices/shipmentSlice";
import { usePrivilege } from "../../../guard/usePrivilege";
import { fetchWarehousesByLocationId } from "../Appointments/TabContent/api/AppointmentsApi";
import {
  fetchYardSettings,
  fetchTrailerLocations,
  fetchTrailersGrid,
  updateYardSlotAvailability,
  getOfficeCode,
  fetchHostlerJobQueue,
  fetchHostlerRoster,
  createHostlerJob,
  overrideHostlerJob,
} from "./api";
import { extractTrailerLocationSlots, filterLocationSlotsByOfficeCode } from "./yardLocationsUtils";
import { mergePendingCheckInTrailers } from "./yardCheckInTrailerGrid";
import {
  YARD_SLOT_STATUS,
  buildYardSlotCards,
  yardSettingsHasLayout,
  formatCheckInTimeDisplay,
  formatDwellTime,
  formatShipmentTypeDisplay,
  normalizeYardSettingsSections,
  resolveLocationParentId,
  isTrailerCheckedIn,
} from "./yardViewUtils";

const REFRESH_MS = 20000;
const YARD_VIEW_STORAGE_KEY = "yms_yard_view_selection";

const LOAD_TYPE_DISPLAY = {
  DROP_TRAILER: "Drop Trailer",
  LIVE_LOAD: "Live Load",
};

// Both load types move through a Hostler job (LGX-1158). Matched loosely, like the backend's
// normalizeLoadType, because a trailer row can hold "Live Load" as well as "LIVE_LOAD".
const canHaveHostlerJob = (loadType) => {
  const normalized = String(loadType ?? "").toUpperCase();
  return normalized.includes("DROP") || normalized.includes("LIVE");
};

const CONDITION_DISPLAY = {
  LOADED: "Loaded",
  EMPTY: "Empty",
  PARTIALLY_EMPTY: "Partially Empty",
  PARTIALLY_LOADED: "Partially Loaded",
  UNLOADED: "Unloaded",
};

const getLocKey = (loc) => loc?.uid ?? loc?.id ?? loc?.locationRef;

function readYardViewSelection() {
  try {
    const raw = sessionStorage.getItem(YARD_VIEW_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeYardViewSelection(selection) {
  try {
    sessionStorage.setItem(YARD_VIEW_STORAGE_KEY, JSON.stringify(selection));
  } catch {
    // ignore quota errors
  }
}

const CARD_STYLES = {
  [YARD_SLOT_STATUS.EMPTY]: {
    background: "#dcfce7",
    border: "2px solid #22c55e",
    color: "#166534",
  },
  [YARD_SLOT_STATUS.OCCUPIED]: {
    background: "#fee2e2",
    border: "2px solid #ef4444",
    color: "#991b1b",
  },
  [YARD_SLOT_STATUS.UNAVAILABLE]: {
    background: "#e5e7eb",
    border: "2px solid #9ca3af",
    color: "#4b5563",
  },
};

function YardSlotDetailModal({
  card,
  dwellNow,
  onClose,
  onToggleAvailability,
  toggling,
  onOpenTrailer,
  canAssignHostlers,
  onManageHostlerJob,
  warehouseOfficeCode,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  if (!card) return null;
  const isOccupied = card.status === YARD_SLOT_STATUS.OCCUPIED;
  const isUnavailable = card.status === YARD_SLOT_STATUS.UNAVAILABLE;
  const trailer = card.trailer;
  const trailerId = trailer?.id ?? trailer?.trailerId;

  const handleOpenShipment = (shipmentNumber) => {
    // detailed-shipment requires an Office-Code matching one of the shipment's parties; without
    // it the request 400s and the details view spins forever. The yard's warehouse office is
    // normally the shipper or receiver, with the trailer's carrier as the next best guess.
    const shipmentOfficeCode =
      warehouseOfficeCode || trailer?.carrierOfficeCode || getOfficeCode() || undefined;
    dispatch(addShipmentTab({
      id: `shipment-${shipmentNumber}-${Date.now()}`,
      name: `Shipment: ${shipmentNumber}`,
      contentType: "shipmentView",
      shipmentNumber,
      shipmentOfficeCode,
      isDynamic: true,
      createdAt: Date.now(),
    }));
    navigate("/dashboard/shipments");
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl font-roboto dark:bg-acs-dark-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3 dark:border-acs-dark-border">
          <h3 className="text-base font-semibold text-gray-900 dark:text-acs-dark-text">{card.displayLabel}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:text-acs-dark-subtext dark:hover:bg-acs-dark-hover"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3 px-5 py-4 text-sm text-gray-700 dark:text-acs-dark-text">
          <p>
            <span className="font-semibold text-gray-500 dark:text-acs-dark-subtext">Section:</span> {card.sectionName}
          </p>
          <p>
            <span className="font-semibold text-gray-500 dark:text-acs-dark-subtext">Bay:</span> {card.bayName || "—"}
          </p>
          <p>
            <span className="font-semibold text-gray-500 dark:text-acs-dark-subtext">Dock:</span> {card.dockName || "—"}
          </p>

          {isOccupied && trailer && (
            <div className="mt-2 space-y-2 rounded-md border border-red-100 bg-red-50 p-3 dark:border-red-500/30 dark:bg-red-500/10">
              <p>
                <span className="font-semibold text-gray-600 dark:text-acs-dark-subtext">Trailer #:</span>{" "}
                {trailer.trailerNumber || "—"}
              </p>
              <p>
                <span className="font-semibold text-gray-600 dark:text-acs-dark-subtext">Carrier:</span>{" "}
                {trailer.carrierName || "—"}
              </p>
              <p>
                <span className="font-semibold text-gray-600 dark:text-acs-dark-subtext">Carrier Code:</span>{" "}
                {trailer.carrierOfficeCode || "—"}
              </p>
              <p>
                <span className="font-semibold text-gray-600 dark:text-acs-dark-subtext">Shipment:</span>{" "}
                {trailer.shipmentNumber ? (
                  <button
                    type="button"
                    onClick={() => handleOpenShipment(trailer.shipmentNumber)}
                    className="font-semibold text-[#1e2a74] underline hover:opacity-80 dark:text-acs-accent-light"
                  >
                    {trailer.shipmentNumber}
                  </button>
                ) : (
                  "—"
                )}
              </p>
              <p>
                <span className="font-semibold text-gray-600 dark:text-acs-dark-subtext">Origin:</span>{" "}
                {trailer.originName || "—"}
              </p>
              <p>
                <span className="font-semibold text-gray-600 dark:text-acs-dark-subtext">Destination:</span>{" "}
                {trailer.destinationName || "—"}
              </p>
              <p>
                <span className="font-semibold text-gray-600 dark:text-acs-dark-subtext">Load Type:</span>{" "}
                {LOAD_TYPE_DISPLAY[trailer.loadType] || trailer.loadType || "—"}
              </p>
              <p>
                <span className="font-semibold text-gray-600 dark:text-acs-dark-subtext">Direction:</span>{" "}
                {formatShipmentTypeDisplay(trailer.shipmentType)}
              </p>
              <p>
                <span className="font-semibold text-gray-600 dark:text-acs-dark-subtext">Check-in:</span>{" "}
                {formatCheckInTimeDisplay(
                  trailer.checkInTime ?? trailer.checkedInAt ?? trailer.gateCheckInTime
                )}
              </p>
              <p>
                <span className="font-semibold text-gray-600 dark:text-acs-dark-subtext">Dwell (Waiting Time):</span>{" "}
                {trailer.dwellTime ??
                  formatDwellTime(
                    trailer.checkInTime ?? trailer.checkedInAt ?? trailer.gateCheckInTime,
                    dwellNow
                  )}
              </p>
              {trailerId != null && onOpenTrailer && (
                <button
                  type="button"
                  onClick={() => {
                    onOpenTrailer(trailer);
                    onClose();
                  }}
                  className="mt-2 text-sm font-semibold text-[#1e2a74] underline hover:opacity-80 dark:text-acs-accent-light"
                >
                  View trailer details
                </button>
              )}
            </div>
          )}

          {isOccupied && trailer && canAssignHostlers && canHaveHostlerJob(trailer.loadType) && (
            <div className="mt-2 space-y-2 rounded-md border border-indigo-100 bg-indigo-50 p-3">
              {trailer.pendingMoveStatus ? (
                <>
                  <p className="text-xs font-semibold text-indigo-900">
                    Hostler job: {trailer.pendingMoveStatus}
                    {trailer.assignedHostlerEmail ? ` · ${trailer.assignedHostlerEmail}` : " · Unclaimed"}
                  </p>
                  <button
                    type="button"
                    onClick={() => onManageHostlerJob?.("override", trailer)}
                    className="w-full rounded-md border border-[#212D69] bg-white px-3 py-1.5 text-xs font-semibold text-[#212D69] hover:bg-indigo-100"
                  >
                    Override Hostler Job
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => onManageHostlerJob?.("create", trailer)}
                  className="w-full rounded-md border border-[#212D69] bg-[#212D69] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#324EC6]"
                >
                  <Truck size={14} className="mr-1 inline-block" /> Create Hostler Job
                </button>
              )}
              {trailer.issueFlag && (
                <p className="flex items-center gap-1 text-xs font-semibold text-red-700">
                  <AlertTriangle size={13} /> Issue reported on this trailer
                </p>
              )}
            </div>
          )}

          {!isOccupied && (
            <div className="pt-2">
              {isUnavailable ? (
                <button
                  type="button"
                  disabled={toggling}
                  onClick={() => onToggleAvailability(card, false)}
                  className="w-full rounded-md border border-green-600 bg-green-50 px-4 py-2 text-sm font-semibold text-green-800 hover:bg-green-100 disabled:opacity-50"
                >
                  {toggling ? "Updating…" : "Mark as Available"}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={toggling}
                  onClick={() => onToggleAvailability(card, true)}
                  className="w-full rounded-md border border-gray-500 bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                >
                  {toggling ? "Updating…" : "Mark as Unavailable"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Create or override a Hostler job — destination is always a spot NAME, resolved server-side, never a numeric id. */
function HostlerJobModal({ mode, trailer, job, warehouseOptions, currentWarehouseRef, onClose, onSubmit, submitting }) {
  const [toSpotLabel, setToSpotLabel] = useState("");
  const [toWarehouseRef, setToWarehouseRef] = useState(currentWarehouseRef || "");
  const [assignEmail, setAssignEmail] = useState("");
  const [action, setAction] = useState("REASSIGN");
  const [note, setNote] = useState("");

  const isOverride = mode === "override";
  const crossWarehouseWarning =
    toWarehouseRef && currentWarehouseRef && toWarehouseRef !== currentWarehouseRef;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isOverride) {
      if (!note.trim()) {
        notify.error({ title: "Note required", message: "A note is required for every override or adjustment." });
        return;
      }
      onSubmit({
        mode,
        action,
        toSpotLabel: action === "CHANGE_DESTINATION" ? toSpotLabel : undefined,
        toWarehouseRef: action === "CHANGE_DESTINATION" ? toWarehouseRef : undefined,
        reassignToHostlerEmail: action === "REASSIGN" ? assignEmail : undefined,
        note,
      });
      return;
    }
    if (!toSpotLabel.trim()) {
      notify.error({ title: "Destination required", message: "Enter a destination bay/dock name." });
      return;
    }
    onSubmit({ mode, toSpotLabel, toWarehouseRef, assignToHostlerEmail: assignEmail, note });
  };

  return (
    <div
      className="fixed inset-0 z-[210] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-lg bg-white shadow-xl font-roboto dark:bg-acs-dark-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3 dark:border-acs-dark-border">
          <h3 className="text-base font-semibold text-gray-900 dark:text-acs-dark-text">
            {isOverride ? `Override Hostler Job — ${job?.trailerNumber ?? ""}` : `New Hostler Job — ${trailer?.trailerNumber ?? ""}`}
          </h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:text-acs-dark-subtext dark:hover:bg-acs-dark-hover" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3 px-5 py-4 text-sm text-gray-700 dark:text-acs-dark-text">
          {isOverride && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-gray-500 dark:text-acs-dark-subtext">Action</label>
              <select
                value={action}
                onChange={(e) => setAction(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm dark:border-acs-dark-border dark:bg-acs-dark-bg dark:text-acs-dark-text"
              >
                <option value="REASSIGN">Reassign to a different Hostler</option>
                <option value="CHANGE_DESTINATION">Change destination (incl. cross-warehouse)</option>
                <option value="FORCE_COMPLETE">Force-complete (mark as done)</option>
                <option value="CANCEL">Cancel job</option>
              </select>
            </div>
          )}

          {(!isOverride || action === "REASSIGN") && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-gray-500 dark:text-acs-dark-subtext">
                {isOverride ? "Reassign to (email)" : "Assign to Hostler (email, optional)"}
              </label>
              <input
                type="email"
                value={assignEmail}
                onChange={(e) => setAssignEmail(e.target.value)}
                placeholder="hostler@example.com — leave blank for shared queue"
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm dark:border-acs-dark-border dark:bg-acs-dark-bg dark:text-acs-dark-text"
              />
            </div>
          )}

          {(!isOverride || action === "CHANGE_DESTINATION") && (
            <>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-gray-500 dark:text-acs-dark-subtext">Destination warehouse</label>
                <select
                  value={toWarehouseRef}
                  onChange={(e) => setToWarehouseRef(e.target.value)}
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm dark:border-acs-dark-border dark:bg-acs-dark-bg dark:text-acs-dark-text"
                >
                  {(warehouseOptions || []).map((w) => (
                    <option key={w.locationRef} value={w.locationRef}>
                      {w.locationName}
                      {w.locationRef === currentWarehouseRef ? " (current)" : ""}
                    </option>
                  ))}
                </select>
                {crossWarehouseWarning && (
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                    Different warehouse — this becomes a cross-warehouse Hostler move.
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-gray-500 dark:text-acs-dark-subtext">Destination spot (bay/dock name)</label>
                <input
                  type="text"
                  value={toSpotLabel}
                  onChange={(e) => setToSpotLabel(e.target.value)}
                  placeholder="e.g. B-04"
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm dark:border-acs-dark-border dark:bg-acs-dark-bg dark:text-acs-dark-text"
                />
              </div>
            </>
          )}

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase text-gray-500 dark:text-acs-dark-subtext">
              Note {isOverride ? "(required)" : "(optional)"}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={isOverride ? "Why is this being overridden?" : "Any context for the Hostler"}
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm dark:border-acs-dark-border dark:bg-acs-dark-bg dark:text-acs-dark-text"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3 dark:border-acs-dark-border">
          <button type="button" onClick={onClose} className="rounded px-3 py-1.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 dark:text-acs-dark-subtext dark:hover:bg-acs-dark-hover">
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded bg-[#212D69] px-4 py-1.5 text-sm font-semibold text-white hover:bg-[#324EC6] disabled:opacity-50"
          >
            {submitting ? "Saving…" : isOverride ? "Confirm override" : "Create job"}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Confirm-before-stacking prompt, shown when a job is dropped onto a hostler who is
 * off-shift and/or already has an active job. Replaces window.confirm() — pattern
 * mirrors Orders/Modal/CancelOrderModal.js (icon header, warning banner, button row,
 * framer-motion overlay) with full dark-mode support added throughout, matching this
 * app's convention (see ACS-Auth/NotFoundPage.js).
 */
function StackAssignConfirmModal({ hostlerName, reasons, onCancel, onConfirm }) {
  return (
    <motion.div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-30 px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="w-full max-w-md space-y-5 rounded-2xl bg-white p-6 shadow-xl dark:bg-acs-dark-surface"
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
      >
        <div className="flex flex-col items-center space-y-3">
          <div className="rounded-full bg-amber-500 p-3 text-white dark:bg-amber-500/90">
            <AlertTriangle size={28} />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-acs-dark-text">
            Assign anyway?
          </h2>
        </div>

        <div className="space-y-1 rounded-md border-l-4 border-amber-500 bg-amber-50 px-4 py-3 dark:border-amber-500/80 dark:bg-amber-500/10">
          <p className="text-sm text-gray-900 dark:text-acs-dark-text">
            <span className="font-bold">{hostlerName}</span>{" "}
            {reasons.join(" and ")}.
          </p>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-acs-dark-border">
          <Button text="Cancel" variant="outline" onClick={onCancel} />
          <Button text="Assign Anyway" onClick={onConfirm} />
        </div>
      </motion.div>
    </motion.div>
  );
}

/**
 * Trailer picker for the Job Queue panel's "+ New Job" button — lets a dispatcher start
 * a Hostler job directly, without first finding and clicking the trailer's occupied grid
 * slot. Only lists checked-in Drop Trailers with no job already open.
 */
function NewJobTrailerPickerModal({ trailers, onClose, onSelect }) {
  const [search, setSearch] = useState("");

  const filtered = trailers.filter((t) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      String(t.trailerNumber ?? "").toLowerCase().includes(q) ||
      String(t.originName ?? "").toLowerCase().includes(q) ||
      String(t.destinationName ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <motion.div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-30 px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-xl dark:bg-acs-dark-surface"
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3.5 dark:border-acs-dark-border">
          <h2 className="text-base font-semibold text-gray-900 dark:text-acs-dark-text">
            New Hostler Job — select a trailer
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:text-acs-dark-subtext dark:hover:bg-acs-dark-hover"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 pt-3.5">
          <input
            type="text"
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by trailer number, origin, or destination…"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#223688] focus:outline-none focus:ring-1 focus:ring-[#223688]/40 dark:border-acs-dark-border dark:bg-acs-dark-bg dark:text-acs-dark-text"
          />
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3.5">
          {trailers.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400 dark:text-acs-dark-subtext">
              No checked-in Drop Trailer without an open job right now.
            </p>
          ) : filtered.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400 dark:text-acs-dark-subtext">
              No trailers match "{search}".
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {filtered.map((t) => (
                <button
                  key={t.id ?? t.trailerId}
                  type="button"
                  onClick={() => onSelect(t)}
                  className="flex flex-col items-start rounded-lg border border-gray-200 px-3 py-2.5 text-left hover:border-[#223688] hover:bg-gray-50 dark:border-acs-dark-border dark:hover:bg-acs-dark-hover"
                >
                  <span className="text-sm font-semibold text-gray-900 dark:text-acs-dark-text">
                    {t.trailerNumber}
                  </span>
                  {(t.originName || t.destinationName) && (
                    <span className="mt-0.5 text-xs text-gray-500 dark:text-acs-dark-subtext">
                      {t.originName ? `↑ ${t.originName}` : ""}
                      {t.originName && t.destinationName ? "  ·  " : ""}
                      {t.destinationName ? `↓ ${t.destinationName}` : ""}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

/** Bay/dock card grid for the Overview tab. */
const YardOverviewPanel = ({
  locations,
  isLoadingLocations,
  onGoToSettings,
  onYardLayoutDetected,
  onOpenTrailer,
  refreshKey = 0,
  autoRefreshEnabled = true,
  refreshIntervalMs = REFRESH_MS,
}) => {
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedWarehouseRef, setSelectedWarehouseRef] = useState("");
  const [selectedWarehouseName, setSelectedWarehouseName] = useState("");
  const [selectedWarehouseOfficeCode, setSelectedWarehouseOfficeCode] = useState("");
  const [selectedSectionFilter, setSelectedSectionFilter] = useState("ALL");
  const [warehouses, setWarehouses] = useState([]);
  const [isLoadingWarehouses, setIsLoadingWarehouses] = useState(false);
  const [yardSections, setYardSections] = useState([]);
  const [yardLayoutReady, setYardLayoutReady] = useState(false);
  const [isLoadingYard, setIsLoadingYard] = useState(false);
  const [locationSlots, setLocationSlots] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [togglingSlotId, setTogglingSlotId] = useState(null);
  const [dwellNow, setDwellNow] = useState(() => Date.now());
  const [loadError, setLoadError] = useState("");
  const { hasPrivilege } = usePrivilege();
  const canAssignHostlers = hasPrivilege("HOSTLER:ASSIGN_POST");
  const [hostlerJobs, setHostlerJobs] = useState([]);
  const [hostlerRoster, setHostlerRoster] = useState([]);
  const [dragOverHostlerEmail, setDragOverHostlerEmail] = useState(null);
  // null = show all jobs; "__UNASSIGNED__" = only unclaimed jobs; anything else = that hostler's own jobs.
  const [jobQueueFilter, setJobQueueFilter] = useState(null);
  const [jobModal, setJobModal] = useState(null); // { mode: 'create'|'override', trailer, job }
  const [submittingJob, setSubmittingJob] = useState(false);
  const [pendingStackConfirm, setPendingStackConfirm] = useState(null); // { job, hostlerEmail, hostlerName, reasons }
  const [showNewJobPicker, setShowNewJobPicker] = useState(false);

  const [infoData, setInfoData] = useState({
    Location: {
      label: "Location",
      type: "dropdown",
      value: "Select Location",
      options: [],
      isPlaceholder: true,
    },
    Warehouse: {
      label: "Warehouse",
      type: "dropdown",
      value: "Select Warehouse",
      options: [],
      isPlaceholder: true,
    },
    Section: {
      label: "Section",
      type: "dropdown",
      value: "All Sections",
      options: ["All Sections"],
      isPlaceholder: false,
    },
  });

  const selectedLocation = locations.find((l) => getLocKey(l) === selectedLocationId);
  const selectedWarehouse = warehouses.find((w) => w.locationRef === selectedWarehouseRef);

  const sectionFilterOptions = useMemo(() => {
    const names = yardSections.map((s) => String(s?.name ?? "").trim()).filter(Boolean);
    return ["All Sections", ...names];
  }, [yardSections]);

  useEffect(() => {
    setInfoData((prev) => ({
      ...prev,
      Location: {
        ...prev.Location,
        value: isLoadingLocations
          ? "Loading locations..."
          : selectedLocation?.locationName || "Select Location",
        options: locations.map((l) => l.locationName),
        isPlaceholder: isLoadingLocations || !selectedLocation,
      },
      Warehouse: {
        ...prev.Warehouse,
        value: isLoadingWarehouses
          ? "Loading..."
          : selectedWarehouse?.locationName || "Select Warehouse",
        options: selectedLocation && !isLoadingWarehouses
          ? warehouses.map((w) => w.locationName)
          : [],
        isPlaceholder: isLoadingWarehouses || !selectedLocationId || !selectedWarehouse,
      },
      Section: {
        ...prev.Section,
        value: selectedSectionFilter === "ALL" ? "All Sections" : selectedSectionFilter,
        options: sectionFilterOptions,
        isPlaceholder: !yardLayoutReady,
      },
    }));
  }, [
    locations,
    selectedLocationId,
    selectedLocation,
    warehouses,
    selectedWarehouse,
    selectedWarehouseRef,
    isLoadingLocations,
    isLoadingWarehouses,
    selectedSectionFilter,
    sectionFilterOptions,
    yardLayoutReady,
  ]);

  const loadYardData = useCallback(async () => {
    const whName = selectedWarehouseName.trim();
    if (!whName) {
      setYardSections([]);
      setYardLayoutReady(false);
      setLocationSlots([]);
      setTrailers([]);
      return;
    }

    setIsLoadingYard(true);
    setLoadError("");
    const officeCode = String(
      selectedWarehouseOfficeCode ||
        selectedLocation?.officeCode ||
        getOfficeCode() ||
        ""
    ).trim();

    try {
      const [settings, locRaw, gridRaw] = await Promise.all([
        fetchYardSettings(whName),
        fetchTrailerLocations().catch(() => []),
        fetchTrailersGrid().catch(() => []),
      ]);

      const sections = normalizeYardSettingsSections(settings);
      const hasLayout = yardSettingsHasLayout(settings);
      setYardSections(sections);
      setYardLayoutReady(hasLayout);
      if (hasLayout) onYardLayoutDetected?.(true);

      setLocationSlots(
        filterLocationSlotsByOfficeCode(
          extractTrailerLocationSlots(locRaw),
          officeCode
        )
      );
      setTrailers(mergePendingCheckInTrailers(Array.isArray(gridRaw) ? gridRaw : []));

      if (canAssignHostlers && officeCode) {
        fetchHostlerJobQueue([officeCode])
          .then((jobs) => setHostlerJobs(Array.isArray(jobs) ? jobs : []))
          .catch(() => setHostlerJobs([]));
        fetchHostlerRoster([officeCode])
          .then((roster) => setHostlerRoster(Array.isArray(roster) ? roster : []))
          .catch(() => setHostlerRoster([]));
      } else {
        setHostlerJobs([]);
        setHostlerRoster([]);
      }
    } catch (err) {
      console.error("Failed to load yard view:", err);
      setYardSections([]);
      setYardLayoutReady(false);
      setLocationSlots([]);
      setLoadError(
        err?.response?.data?.message ||
          err?.response?.data?.content ||
          "Could not load yard layout. Confirm yard settings are saved for this warehouse."
      );
    } finally {
      setIsLoadingYard(false);
    }
  }, [selectedWarehouseName, selectedWarehouseOfficeCode, selectedLocation, onYardLayoutDetected, canAssignHostlers]);

  useEffect(() => {
    loadYardData();
  }, [loadYardData, refreshKey]);

  useEffect(() => {
    const onCheckIn = () => loadYardData();
    window.addEventListener("yard-trailer-check-in", onCheckIn);
    return () => window.removeEventListener("yard-trailer-check-in", onCheckIn);
  }, [loadYardData]);

  useEffect(() => {
    if (!selectedWarehouseName || !yardLayoutReady || !autoRefreshEnabled) return undefined;
    const id = window.setInterval(loadYardData, refreshIntervalMs);
    return () => window.clearInterval(id);
  }, [selectedWarehouseName, yardLayoutReady, loadYardData, autoRefreshEnabled, refreshIntervalMs]);

  useEffect(() => {
    const id = window.setInterval(() => setDwellNow(Date.now()), 60000);
    return () => window.clearInterval(id);
  }, []);

  const pickWarehouseAfterLoad = useCallback(
    (locId, warehouseList) => {
      if (!warehouseList?.length) return;
      const saved = readYardViewSelection();
      const fromSaved =
        saved?.locationId === locId && saved?.warehouseRef
          ? warehouseList.find((w) => w.locationRef === saved.warehouseRef)
          : null;
      const wh = fromSaved || (warehouseList.length === 1 ? warehouseList[0] : null);
      if (!wh) return;
      setSelectedWarehouseRef(wh.locationRef);
      setSelectedWarehouseName(wh.locationName || "");
      setSelectedWarehouseOfficeCode(wh.officeCode || "");
      writeYardViewSelection({
        locationId: locId,
        warehouseRef: wh.locationRef,
        warehouseName: wh.locationName,
      });
    },
    []
  );

  const handleLocationChange = useCallback(
    async (locId) => {
      setSelectedLocationId(locId);
      setSelectedWarehouseRef("");
      setSelectedWarehouseName("");
      setSelectedWarehouseOfficeCode("");
      setSelectedSectionFilter("ALL");
      setWarehouses([]);
      setYardSections([]);
      setYardLayoutReady(false);
      setSelectedCard(null);
      if (!locId) return;

      const loc = locations.find((l) => getLocKey(l) === locId);
      const parentId = resolveLocationParentId(loc);
      if (!parentId) return;

      setIsLoadingWarehouses(true);
      try {
        const data = await fetchWarehousesByLocationId(parentId);
        const list = data ?? [];
        setWarehouses(list);
        pickWarehouseAfterLoad(locId, list);
      } catch (err) {
        console.error("Failed to load warehouses:", err);
      } finally {
        setIsLoadingWarehouses(false);
      }
    },
    [locations, pickWarehouseAfterLoad]
  );

  useEffect(() => {
    if (!locations?.length || selectedLocationId) return;
    const saved = readYardViewSelection();
    const loc =
      (saved?.locationId &&
        locations.find((l) => getLocKey(l) === saved.locationId)) ||
      (locations.length === 1 ? locations[0] : null);
    if (loc) {
      handleLocationChange(getLocKey(loc));
    }
  }, [locations, selectedLocationId, handleLocationChange]);

  const handleWarehouseChange = useCallback((warehouseRef, warehouseName, officeCode) => {
    setSelectedWarehouseRef(warehouseRef);
    setSelectedWarehouseName(warehouseName || "");
    setSelectedWarehouseOfficeCode(officeCode || "");
    setSelectedSectionFilter("ALL");
    setYardSections([]);
    setYardLayoutReady(false);
    setSelectedCard(null);
    if (warehouseRef && selectedLocationId) {
      writeYardViewSelection({
        locationId: selectedLocationId,
        warehouseRef,
        warehouseName,
      });
    }
  }, [selectedLocationId]);

  const handleInfoChange = useCallback(
    async (field, newValue) => {
      if (field === "Location") {
        const loc = locations.find((l) => l.locationName === newValue);
        if (!loc) return;
        await handleLocationChange(getLocKey(loc));
        return;
      }
      if (field === "Warehouse") {
        const wh = warehouses.find((w) => w.locationName === newValue);
        if (!wh) return;
        handleWarehouseChange(wh.locationRef, wh.locationName, wh.officeCode);
        return;
      }
      if (field === "Section") {
        setSelectedSectionFilter(newValue === "All Sections" ? "ALL" : newValue);
      }
    },
    [locations, warehouses, handleLocationChange, handleWarehouseChange]
  );

  const allCards = useMemo(
    () => buildYardSlotCards({ sections: yardSections, locationSlots, trailers }),
    [yardSections, locationSlots, trailers]
  );

  const visibleCards = useMemo(() => {
    if (selectedSectionFilter === "ALL") return allCards;
    return allCards.filter((c) => c.sectionName === selectedSectionFilter);
  }, [allCards, selectedSectionFilter]);

  const cardsBySection = useMemo(() => {
    const map = new Map();
    for (const card of visibleCards) {
      if (!map.has(card.sectionName)) map.set(card.sectionName, []);
      map.get(card.sectionName).push(card);
    }
    return map;
  }, [visibleCards]);

  const handleToggleAvailability = async (card, unavailable) => {
    setTogglingSlotId(card.id);
    try {
      await updateYardSlotAvailability({
        officeCode: getOfficeCode(),
        warehouseRef: selectedWarehouseRef,
        sectionName: card.sectionName,
        slotType: card.slotType,
        slotName: card.slotName,
        unavailable,
        bayId: card.bayId,
        dockId: card.dockId,
      });
      notify.success({
        title: unavailable ? "Marked unavailable" : "Marked available",
        message: `${card.displayLabel} is now ${unavailable ? "unavailable" : "available"}.`,
      });
      setSelectedCard(null);
      await loadYardData();
    } catch (err) {
      notify.error({
        title: "Update failed",
        message:
          err?.response?.data?.message ||
          err?.response?.data?.content ||
          err?.message ||
          "Could not update slot availability.",
      });
    } finally {
      setTogglingSlotId(null);
    }
  };

  const handleManageHostlerJob = (mode, trailer) => {
    const job = hostlerJobs.find((j) => j.trailerId === (trailer.id ?? trailer.trailerId));
    setJobModal({ mode, trailer, job });
  };

  const handleJobModalSubmit = async (payload) => {
    setSubmittingJob(true);
    try {
      if (payload.mode === "create") {
        await createHostlerJob({
          trailerId: jobModal.trailer.id ?? jobModal.trailer.trailerId,
          toSpotLabel: payload.toSpotLabel,
          toWarehouseRef: payload.toWarehouseRef,
          assignToHostlerEmail: payload.assignToHostlerEmail,
          note: payload.note,
        });
        notify.success({ title: "Hostler job created", message: "The job is now in the queue." });
      } else {
        await overrideHostlerJob({
          trailerId: jobModal.job.trailerId,
          action: payload.action,
          toSpotLabel: payload.toSpotLabel,
          toWarehouseRef: payload.toWarehouseRef,
          reassignToHostlerEmail: payload.reassignToHostlerEmail,
          note: payload.note,
        });
        notify.success({ title: "Hostler job updated", message: "Override applied." });
      }
      setJobModal(null);
      setSelectedCard(null);
      await loadYardData();
    } catch (err) {
      notify.error({
        title: "Could not save Hostler job",
        message:
          err?.response?.data?.message ||
          err?.response?.data?.content ||
          err?.message ||
          "Something went wrong.",
      });
    } finally {
      setSubmittingJob(false);
    }
  };

  const handleAssignViaDrop = async (job, hostlerEmail) => {
    setSubmittingJob(true);
    try {
      await overrideHostlerJob({
        trailerId: job.trailerId,
        action: "REASSIGN",
        reassignToHostlerEmail: hostlerEmail,
        note: "Assigned via drag-and-drop",
      });
      notify.success({ title: "Hostler assigned", message: `${job.trailerNumber} assigned.` });
      await loadYardData();
    } catch (err) {
      notify.error({
        title: "Could not assign Hostler",
        message:
          err?.response?.data?.message ||
          err?.response?.data?.content ||
          err?.message ||
          "Something went wrong.",
      });
    } finally {
      setSubmittingJob(false);
    }
  };

  const filteredHostlerJobs =
    jobQueueFilter === null
      ? hostlerJobs
      : jobQueueFilter === "__UNASSIGNED__"
      ? hostlerJobs.filter((job) => !job.assignedHostlerEmail)
      : hostlerJobs.filter(
          (job) => (job.assignedHostlerEmail || "").toLowerCase() === jobQueueFilter.toLowerCase()
        );

  // Trailers eligible to have a NEW Hostler job created directly from the queue panel's
  // "+ New Job" button, without first finding and clicking their occupied grid slot.
  const eligibleTrailersForNewJob = trailers.filter(
    (t) => canHaveHostlerJob(t.loadType) && isTrailerCheckedIn(t) && !t.pendingMoveStatus
  );

  const legend = (
    <div className="mb-4 flex flex-wrap items-center gap-4 text-xs text-gray-600 dark:text-acs-dark-subtext">
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-3 w-3 rounded-sm border border-green-500 bg-green-200" />
        Empty / available
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-3 w-3 rounded-sm border border-red-500 bg-red-200" />
        Occupied
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-3 w-3 rounded-sm border border-gray-500 bg-gray-300" />
        Unavailable
      </span>
      <span className="text-gray-400 dark:text-acs-dark-subtext">
        {autoRefreshEnabled
          ? `Auto-refreshes every ${refreshIntervalMs / 1000}s`
          : "Auto-refresh paused"}
      </span>
    </div>
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="sticky top-0 z-50 w-full shrink-0 bg-white shadow-md dark:bg-acs-dark-surface">
        <StickyHeader
          mode={{}}
          infoData={infoData}
          onInfoChange={handleInfoChange}
          buttons={[
            {
              label: "Settings",
              color: "blue",
              icon: <Settings size={16} className="shrink-0" />,
              onClick: onGoToSettings,
            },
          ]}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-white p-6 dark:bg-acs-dark-bg">
        {!locations?.length && !isLoadingLocations ? (
          <p className="text-[13px] text-gray-400 dark:text-acs-dark-subtext">No locations available for your office.</p>
        ) : !selectedWarehouseRef ? (
          <p className="text-[13px] text-gray-400 dark:text-acs-dark-subtext">
            Select a location and warehouse to view your yard.
          </p>
        ) : isLoadingYard ? (
          <p className="text-[13px] text-gray-400 dark:text-acs-dark-subtext">Loading yard view…</p>
        ) : loadError ? (
          <p className="text-[13px] text-red-600">{loadError}</p>
        ) : !yardLayoutReady ? (
          <p className="text-[13px] text-gray-400 dark:text-acs-dark-subtext">
            Complete yard setup with at least one bay or dock in{" "}
            <button
              type="button"
              onClick={onGoToSettings}
              className="cursor-pointer border-0 bg-transparent p-0 font-inherit text-[#1e2a74] underline dark:text-acs-accent-light"
            >
              Yard Settings
            </button>{" "}
            to see the yard map here.
          </p>
        ) : (
          <>
            {legend}
            <div
              className={
                canAssignHostlers
                  ? "grid grid-cols-1 gap-6 lg:grid-cols-[1.15fr_1fr] lg:items-start"
                  : ""
              }
            >
            <div>
            {[...cardsBySection.entries()].map(([sectionName, cards]) => (
              <div key={sectionName} className="mb-8">
                <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-acs-dark-subtext">
                  {sectionName}
                </h4>
                <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, 150px)" }}>
                  {cards.map((card) => {
                    const style = CARD_STYLES[card.status] || CARD_STYLES[YARD_SLOT_STATUS.EMPTY];
                    const trailer = card.trailer;
                    const isOccupied = card.status === YARD_SLOT_STATUS.OCCUPIED && trailer;
                    const checkInTime = trailer
                      ? trailer.checkInTime ?? trailer.checkedInAt ?? trailer.gateCheckInTime
                      : null;
                    const hasOpenJob = isOccupied && trailer.pendingMoveStatus;
                    const hasIssue = isOccupied && trailer.issueFlag;
                    return (
                      <button
                        key={card.id}
                        type="button"
                        onClick={() => setSelectedCard(card)}
                        className="relative flex h-28 w-full flex-col justify-center rounded-lg px-3 py-2 text-center font-semibold transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#223688]/40"
                        style={{
                          ...style,
                          boxShadow: hasOpenJob ? "0 0 0 2px #212D69" : style.border ? undefined : undefined,
                        }}
                      >
                        {hasOpenJob && (
                          <span className="absolute -top-2 left-2 rounded-full bg-[#212D69] px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">
                            Move
                          </span>
                        )}
                        {hasIssue && (
                          <span className="absolute -top-2 right-2 flex h-4 w-4 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white">
                            !
                          </span>
                        )}
                        <span className="block text-sm">{card.displayLabel}</span>
                        {isOccupied && trailer.trailerNumber && (
                          <span className="mt-1 block truncate text-[11px] font-normal opacity-90">
                            {trailer.trailerNumber}
                          </span>
                        )}
                        {isOccupied && trailer.originName && (
                          <span className="mt-1 block truncate text-left text-[10px] font-normal normal-case leading-tight opacity-90">
                            ↑ {trailer.originName}
                          </span>
                        )}
                        {isOccupied && trailer.destinationName && (
                          <span className="block truncate text-left text-[10px] font-normal normal-case leading-tight opacity-90">
                            ↓ {trailer.destinationName}
                          </span>
                        )}
                        {isOccupied && (trailer.loadType || trailer.status || checkInTime) && (
                          <span className="mt-1 block truncate text-[10px] font-normal normal-case opacity-90">
                            {LOAD_TYPE_DISPLAY[trailer.loadType] || trailer.loadType || "—"}
                            {trailer.status ? ` · ${CONDITION_DISPLAY[trailer.status] || trailer.status}` : ""}
                            {trailer.dwellTime
                              ? ` · ${trailer.dwellTime}`
                              : checkInTime
                                ? ` · ${formatDwellTime(checkInTime, dwellNow)}`
                                : ""}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {visibleCards.length === 0 && (
              <p className="text-[13px] text-gray-400 dark:text-acs-dark-subtext">No slots match the selected section filter.</p>
            )}
            </div>

            {canAssignHostlers && (
              <div className="flex flex-col gap-6 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-140px)] lg:overflow-y-auto">
              <div className="rounded-lg border border-gray-200 dark:border-acs-dark-border dark:bg-acs-dark-surface">
                <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2.5 dark:border-acs-dark-border dark:bg-acs-dark-hover">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-acs-dark-text">
                    Hostlers on shift
                  </h4>
                  <span className="text-[11px] text-gray-400 dark:text-acs-dark-subtext">
                    Drag a job onto an idle hostler · click a name to filter the queue below
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 p-3">
                  {hostlerRoster.length === 0 ? (
                    <p className="px-1 py-1 text-[13px] text-gray-400 dark:text-acs-dark-subtext">
                      No Hostlers assigned to this office yet.
                    </p>
                  ) : (
                    hostlerRoster.map((h) => {
                      const isIdle = h.status === "IDLE";
                      const isOffShift = h.status === "OFF_SHIFT";
                      const isDragTarget = dragOverHostlerEmail === h.email;
                      const isFilterActive = jobQueueFilter === h.email;
                      const activeJobs = h.activeJobTrailerNumbers || [];
                      const dotColor = isIdle ? "bg-green-500" : isOffShift ? "bg-amber-500" : "bg-gray-400";
                      return (
                        <button
                          type="button"
                          key={h.email}
                          onClick={() =>
                            setJobQueueFilter((cur) => (cur === h.email ? null : h.email))
                          }
                          onDragOver={(e) => {
                            e.preventDefault();
                            setDragOverHostlerEmail(h.email);
                          }}
                          onDragLeave={() => setDragOverHostlerEmail((cur) => (cur === h.email ? null : cur))}
                          onDrop={(e) => {
                            e.preventDefault();
                            setDragOverHostlerEmail(null);
                            const trailerId = Number(e.dataTransfer.getData("text/hostler-job-trailer-id"));
                            const job = hostlerJobs.find((j) => j.trailerId === trailerId);
                            if (!job) return;
                            if (isOffShift || !isIdle) {
                              const reasons = [];
                              if (isOffShift) reasons.push("is currently off shift");
                              if (!isIdle && activeJobs.length > 0) {
                                reasons.push(`already has a job assigned (${activeJobs.join(", ")})`);
                              }
                              setPendingStackConfirm({ job, hostlerEmail: h.email, hostlerName: h.name, reasons });
                              return;
                            }
                            handleAssignViaDrop(job, h.email);
                          }}
                          title="Click to filter the job queue to this hostler's jobs"
                          className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${
                            isFilterActive
                              ? "border-[#212D69] bg-[#212D69]/10 dark:border-acs-accent dark:bg-acs-accent/10"
                              : isDragTarget
                              ? "border-[#31C4AB] bg-[#31C4AB]/10"
                              : "border-dashed border-gray-300 dark:border-acs-dark-border"
                          } ${isIdle ? "" : "opacity-70"} dark:text-acs-dark-text`}
                        >
                          <span className={`h-2 w-2 rounded-full ${dotColor}`} />
                          {h.name}
                          {isOffShift && (
                            <span className="text-amber-600 dark:text-amber-400">· Off shift</span>
                          )}
                          {!isOffShift && h.status === "BUSY" && activeJobs.length > 0 && (
                            <span className="text-gray-400 dark:text-acs-dark-subtext">
                              · {activeJobs.join(", ")}
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      setJobQueueFilter((cur) => (cur === "__UNASSIGNED__" ? null : "__UNASSIGNED__"))
                    }
                    title="Click to filter the job queue to unassigned jobs"
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${
                      jobQueueFilter === "__UNASSIGNED__"
                        ? "border-[#212D69] bg-[#212D69]/10 dark:border-acs-accent dark:bg-acs-accent/10"
                        : "border-dashed border-gray-300 dark:border-acs-dark-border"
                    } dark:text-acs-dark-text`}
                  >
                    <span className="h-2 w-2 rounded-full bg-gray-400" />
                    Unassigned
                  </button>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-acs-dark-border dark:bg-acs-dark-surface">
                <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2.5 dark:border-acs-dark-border dark:bg-acs-dark-hover">
                  <div className="flex items-center gap-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-acs-dark-text">
                      Hostler Job Queue
                    </h4>
                    {jobQueueFilter !== null && (
                      <button
                        type="button"
                        onClick={() => setJobQueueFilter(null)}
                        className="rounded-full bg-[#212D69]/10 px-2 py-0.5 text-[10px] font-semibold text-[#212D69] hover:bg-[#212D69]/20 dark:bg-acs-accent/10 dark:text-acs-accent dark:hover:bg-acs-accent/20"
                      >
                        {jobQueueFilter === "__UNASSIGNED__" ? "Unassigned" : jobQueueFilter} ✕
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400 dark:text-acs-dark-subtext">
                      {filteredHostlerJobs.length} job(s)
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowNewJobPicker(true)}
                      title={
                        eligibleTrailersForNewJob.length === 0
                          ? "No checked-in Drop Trailer without an open job right now"
                          : "Create a new Hostler job for a checked-in Drop Trailer"
                      }
                      className="rounded-full bg-[#212D69] px-3 py-1 text-[11px] font-semibold text-white hover:bg-[#324EC6] dark:bg-acs-accent dark:hover:bg-acs-accent/80"
                    >
                      + New Job
                    </button>
                  </div>
                </div>
                {filteredHostlerJobs.length === 0 ? (
                  <p className="px-4 py-4 text-[13px] text-gray-400 dark:text-acs-dark-subtext">
                    {jobQueueFilter === null
                      ? "No open Hostler jobs. Click + New Job above, or open an occupied Drop Trailer slot on the grid."
                      : "No jobs match this filter."}
                  </p>
                ) : (
                  <table className="w-full text-left text-xs">
                    <thead className="text-gray-500 dark:text-acs-dark-subtext">
                      <tr>
                        <th className="px-4 py-2 font-semibold">Trailer</th>
                        <th className="px-4 py-2 font-semibold">Route</th>
                        <th className="px-4 py-2 font-semibold">Hostler</th>
                        <th className="px-4 py-2 font-semibold">Status</th>
                        <th className="px-4 py-2 font-semibold">Age</th>
                        <th className="px-4 py-2 font-semibold"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHostlerJobs.map((job) => {
                        const isPending = job.status === "PENDING";
                        const pillClasses =
                          job.status === "PENDING"
                            ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                            : "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300";
                        return (
                          <tr
                            key={job.trailerId}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData("text/hostler-job-trailer-id", String(job.trailerId));
                              e.dataTransfer.effectAllowed = "move";
                            }}
                            className="cursor-grab border-t border-gray-100 dark:border-acs-dark-border"
                          >
                            <td className="px-4 py-2 font-semibold text-gray-800 dark:text-acs-dark-text">
                              {job.trailerNumber}
                              {job.issueFlag && (
                                <AlertTriangle size={12} className="ml-1 inline text-red-600" />
                              )}
                            </td>
                            <td className="px-4 py-2 text-gray-600 dark:text-acs-dark-subtext">
                              <div>{job.toLocationLabel}</div>
                              <span className="mt-1 inline-block rounded border border-emerald-500 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-700 dark:text-emerald-400">
                                Drop trailer
                              </span>
                              {job.crossWarehouse && (
                                <span className="ml-1 inline-block rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-800">
                                  cross-warehouse
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-gray-600 dark:text-acs-dark-subtext">
                              {job.assignedHostlerName || job.assignedHostlerEmail || (
                                <span className="italic text-gray-400 dark:text-acs-dark-subtext">Unclaimed</span>
                              )}
                            </td>
                            <td className="px-4 py-2">
                              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${pillClasses}`}>
                                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                                {job.status}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-gray-500 dark:text-acs-dark-subtext">
                              {formatDwellTime(job.createdAt, dwellNow)}
                            </td>
                            <td className="px-4 py-2 text-right">
                              <button
                                type="button"
                                onClick={() => setJobModal({ mode: "override", job })}
                                className={
                                  isPending
                                    ? "rounded bg-[#212D69] px-2 py-1 text-[11px] font-semibold text-white hover:bg-[#324EC6]"
                                    : "rounded border border-gray-300 px-2 py-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-100 dark:border-acs-dark-border dark:text-acs-dark-text dark:hover:bg-acs-dark-hover"
                                }
                              >
                                {isPending ? "Assign" : "Override"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              </div>
            )}
            </div>
          </>
        )}
      </div>

      <YardSlotDetailModal
        card={selectedCard}
        dwellNow={dwellNow}
        onClose={() => setSelectedCard(null)}
        onToggleAvailability={handleToggleAvailability}
        onOpenTrailer={onOpenTrailer}
        toggling={!!selectedCard && togglingSlotId === selectedCard.id}
        canAssignHostlers={canAssignHostlers}
        onManageHostlerJob={handleManageHostlerJob}
        warehouseOfficeCode={selectedWarehouseOfficeCode || selectedLocation?.officeCode || ""}
      />

      {jobModal && (
        <HostlerJobModal
          mode={jobModal.mode}
          trailer={jobModal.trailer}
          job={jobModal.job}
          warehouseOptions={warehouses}
          currentWarehouseRef={selectedWarehouseRef}
          onClose={() => setJobModal(null)}
          onSubmit={handleJobModalSubmit}
          submitting={submittingJob}
        />
      )}

      <AnimatePresence>
        {pendingStackConfirm && (
          <StackAssignConfirmModal
            hostlerName={pendingStackConfirm.hostlerName}
            reasons={pendingStackConfirm.reasons}
            onCancel={() => setPendingStackConfirm(null)}
            onConfirm={() => {
              handleAssignViaDrop(pendingStackConfirm.job, pendingStackConfirm.hostlerEmail);
              setPendingStackConfirm(null);
            }}
          />
        )}
        {showNewJobPicker && (
          <NewJobTrailerPickerModal
            trailers={eligibleTrailersForNewJob}
            onClose={() => setShowNewJobPicker(false)}
            onSelect={(trailer) => {
              setShowNewJobPicker(false);
              handleManageHostlerJob("create", trailer);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default YardOverviewPanel;
